/**
 * ticket-inapp.card — the public, variant-keyed dispatch surface across
 * this package's shipped in-app card renderers (`editorial`, `stencil`,
 * `strata`), plus the deterministic sample props a picker/preview surface
 * renders a consistent card against. `TICKET_TEMPLATE_VARIANTS` is derived
 * straight from `@momo/types`' `TicketTemplateVariantSchema` rather than
 * re-listing the variant tokens, so this package's own variant set can
 * never drift from that closed enum — the event/ticket-type
 * `templateVariant` field both resolve against.
 */
import {
	type TicketTemplateVariant,
	TicketTemplateVariantSchema,
} from "@momo/types";
import type { TemplateResult } from "lit";
import type { TicketTemplateProps } from "./ticket.props.ts";
import { renderTicketInAppEditorial } from "./ticket-inapp.editorial.template.ts";
import { renderTicketInAppStencil } from "./ticket-inapp.stencil.template.ts";
import { renderTicketInAppStrata } from "./ticket-inapp.strata.template.ts";

export const TICKET_TEMPLATE_VARIANTS: readonly TicketTemplateVariant[] =
	TicketTemplateVariantSchema.options;

/** Renders `variant`'s in-app 2:3 portrait card for `props` — the one entry
 *  point a card-preview surface calls through, never a variant's own
 *  renderer directly. */
export function renderTicketVariantCard(
	variant: TicketTemplateVariant,
	props: TicketTemplateProps,
): TemplateResult {
	switch (variant) {
		case "editorial":
			return renderTicketInAppEditorial(props);
		case "stencil":
			return renderTicketInAppStencil(props);
		case "strata":
			return renderTicketInAppStrata(props);
	}
}

/** Berlin Design Forum 2026 @ Sample Studio — a deterministic,
 *  representative single-ticket fixture every card preview renders
 *  against, so a variant picker/gallery shows a consistent card
 *  regardless of which variant is selected. */
export const sampleTicketCardProps: TicketTemplateProps = {
	locale: "en",
	brand: {
		// catalog-text-ok: specimen data, not chrome this package renders itself
		name: "Sample Studio",
		logoUrl: null,
		colors: null,
	},
	event: {
		// catalog-text-ok: specimen data, as above
		name: "Berlin Design Forum 2026",
		posterUrl: null,
		start: new Date("2026-11-14T19:00:00.000Z"),
		end: new Date("2026-11-15T05:00:00.000Z"),
		timezone: "Europe/Berlin",
		venue: {
			// catalog-text-ok: specimen data, as above
			name: "Sample Studio",
			address: "Example Street 1, 10000 Sample City",
			notes: null,
		},
	},
	ticket: {
		displayId: "T-000842",
		typeName: "Frühbucher",
		qrToken: "demo-qr-token-abcdef123456",
	},
	sequence: null,
};
